import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, MenuItem, Paper, Stack, TextField, Typography } from "@mui/material";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import { useCan } from "../auth/Can";
import { getApiErrorMessage, toast } from "../lib/toast";
import { BotReply, getBotIntents, intentLabel, postBotPlayground, postBotPlaygroundFeedback } from "../services/supportBotService";

const ACTION_TEXT: Record<string, string> = {
    answer: "Sure → answers",
    clarify: "Unsure → asks which one",
    ask_safety: "Possible safety → asks \"Are you safe?\"",
    p0_handoff: "Safety → hands over to a person",
    not_understood: "Didn't understand → asks to rephrase",
    not_about_us: "Not about rides → polite redirect",
};

const BotPlayground: React.FC = () => {
    const { can } = useCan();
    const canRate = can("support:write");
    const [text, setText] = useState("");
    const [asked, setAsked] = useState("");
    const [result, setResult] = useState<BotReply | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [intents, setIntents] = useState<string[]>([]);
    const [correct, setCorrect] = useState("");
    const [rated, setRated] = useState<"up" | "down" | null>(null);
    const [asUid, setAsUid] = useState("");

    useEffect(() => { getBotIntents().then((r) => setIntents(r.data.intents)).catch(() => undefined); }, []);

    const ask = async () => {
        const t = text.trim();
        if (!t) return;
        setLoading(true); setError(null); setResult(null); setRated(null); setCorrect("");
        try {
            const r = await postBotPlayground(t, asUid.trim() || undefined);
            setResult(r.data); setAsked(t);
        } catch (e) {
            setError(getApiErrorMessage(e, "The support bot didn't answer"));
        } finally {
            setLoading(false);
        }
    };

    const rate = async (r: BotReply, verdict: "up" | "down") => {
        try {
            await postBotPlaygroundFeedback({ text: asked, botIntent: r.intent, verdict, correctIntent: verdict === "down" && correct ? correct : null });
            setRated(verdict);
            toast({ message: "Saved — thank you, this trains the next version", severity: "success" });
        } catch (e) {
            toast({ message: getApiErrorMessage(e, "Couldn't save the rating"), severity: "error" });
        }
    };

    return (
        <Box sx={{ p: 2, maxWidth: 900 }}>
            <Typography variant="h6">Bot playground</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Type a message the way a rider or driver would (English, Hinglish or Hindi). See what the support bot understands and what it would reply. Your ratings train the next version.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField fullWidth size="small" placeholder="e.g. coins withdraw kaise kare" value={text} inputProps={{ maxLength: 500 }}
                    onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") ask(); }} />
                {can("users:read" as any) && <TextField size="small" label="Test as user id (optional)" value={asUid} inputProps={{ maxLength: 128 }} onChange={(e) => setAsUid(e.target.value)} sx={{ minWidth: 220 }} />}
                <Button variant="contained" onClick={ask} disabled={loading || !text.trim()}>{loading ? <CircularProgress size={20} /> : "Ask"}</Button>
            </Stack>
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            {result && (
                <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                        <Chip label={ACTION_TEXT[result.action] ?? result.action} color={result.action === "answer" ? "success" : result.action.includes("safety") || result.action === "p0_handoff" ? "error" : "warning"} />
                        {result.intent && <Chip label={intentLabel(result.intent)} variant="outlined" />}
                        {result.confidence != null && <Chip label={`${Math.round(result.confidence * 100)}% sure`} variant="outlined" />}
                        <Chip label={result.wouldShowLive ? "Customers would see this" : "Customers would get the old flow"} color={result.wouldShowLive ? "success" : "default"} variant="outlined" />
                        <Chip label={`${result.latencyMs} ms · ${result.model?.name ?? "bot"}`} variant="outlined" />
                    </Stack>
                    <Typography variant="subtitle2">Reply ({result.reply.lang})</Typography>
                    <Typography sx={{ whiteSpace: "pre-line", mb: 1 }}>{result.reply.text}</Typography>
                    {result.reply.needsData && !result.filled && <Alert severity="info" sx={{ mb: 1 }}>This reply needs the customer's own data (shown here with sample values). Enter a user id above to see it filled with their data.</Alert>}
                    {result.filled && (
                        !result.filled.found ? <Alert severity="warning" sx={{ mb: 1 }}>No user with id {result.filled.asUid}.</Alert>
                        : result.filled.text ? <Alert severity="success" sx={{ mb: 1 }}>As this {result.filled.role}, customers would see: <strong>{result.filled.text}</strong></Alert>
                        : <Alert severity="warning" sx={{ mb: 1 }}>Can't fill for this {result.filled.role} (missing: {result.filled.missing.join(", ")}) — they would get the old flow.</Alert>
                    )}
                    {canRate && (
                        rated ? (
                            <Alert severity="success">Rated {rated === "up" ? "👍 good" : "👎 poor"}.</Alert>
                        ) : (
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Button startIcon={<ThumbUpAltOutlinedIcon />} onClick={() => rate(result, "up")}>Good</Button>
                                <TextField select size="small" label="Right topic" value={correct} onChange={(e) => setCorrect(e.target.value)} sx={{ minWidth: 220 }}>
                                    <MenuItem value="">—</MenuItem>
                                    {intents.map((i) => <MenuItem key={i} value={i}>{intentLabel(i)}</MenuItem>)}
                                </TextField>
                                <Button color="error" startIcon={<ThumbDownAltOutlinedIcon />} onClick={() => rate(result, "down")}>Poor</Button>
                            </Stack>
                        )
                    )}
                </Paper>
            )}
        </Box>
    );
};

export default BotPlayground;
